import { BigNum, MarketType } from '@velocity-exchange/sdk';
import { UIMarket } from '@velocity-exchange/common';
import { cm } from 'src/utils/classNames';
import MobileTableCard from '../Tables/cards/MobileTableCard';
import MarketCellV2 from '../Tables/cells/MarketCellV2';
import TableV2 from '../Tables/TableV2';
import { Swap } from '@velocity-exchange/icons';
import Button from '../primitives/Button/Button';
import { PnlBreakdownTooltip } from '../Tooltip/PnlBreakdownTooltip';
import DefinitionTooltip from 'src/components/Tooltip/DefinitionTooltip/DefinitionTooltip';
import { TOOLTIP_TRIGGER_DECORATION_CLASSNAME } from 'src/components/primitives/Tooltip/consts';
import IconButton from '../primitives/IconButton/IconButton';
import { signDependentValue } from 'src/utils/signDependentValue';
import useMarketsInfoStore from 'src/stores/useMarketsInfoStore';
import useExpandableItem, {
	ExpandableItemParams,
} from 'src/hooks/lists/expandable/useExpandableItem';

interface PositionHistoryMobileCardProps extends ExpandableItemParams {
	uimarket: UIMarket;
	marketName: string;
	direction: 'long' | 'short';
	pnl: number;
	pnlNotional: string;
	positionPnlNotional: string;
	feesAndFundingNotional: string;
	pricePercentChangeDisplay: string;
	entryPrice: BigNum;
	avgExitPrice: BigNum;
	date: Date;
	displayDatesAsTimestamp: boolean;
	onToggleDateDisplay: () => void;
	onShareClick: () => void;
	baseClosedForPnl?: BigNum | null;
}

export function PositionHistoryMobileCard({
	uimarket,
	marketName,
	direction,
	pnl,
	pnlNotional,
	positionPnlNotional,
	feesAndFundingNotional,
	pricePercentChangeDisplay,
	entryPrice,
	avgExitPrice,
	date,
	displayDatesAsTimestamp,
	onToggleDateDisplay,
	onShareClick,
	baseClosedForPnl,
	areAllItemsExpanded,
	handleItemExpandedChange,
}: PositionHistoryMobileCardProps) {
	const { isExpanded, handleExpandedChange } = useExpandableItem({
		areAllItemsExpanded,
		handleItemExpandedChange,
	});

	const getMarketInfoByIndexAndType = useMarketsInfoStore(
		(s) => s.getMarketInfoByIndexAndType
	);
	const priceDecimalOverride = uimarket.isPerp
		? getMarketInfoByIndexAndType(uimarket.marketIndex, MarketType.PERP)
				?.genericInfo.priceDisplayDecimals
		: undefined;

	return (
		<MobileTableCard.Root
			expanded={isExpanded}
			setExpanded={handleExpandedChange}
		>
			<MobileTableCard.CollapsibleHeader expanded={isExpanded}>
				<div className="flex w-full items-center justify-between">
					<MarketCellV2
						className="py-0"
						direction={direction}
						marketSymbol={marketName}
						marketIndex={uimarket.marketIndex}
						marketType={uimarket.marketType}
						textClass="typo-label-md"
						hideLeverage
						hidePositionType
					/>
					<div
						className="flex flex-col items-end gap-y-xs"
						data-collapsible-card-fade
					>
						<MobileTableCard.Label>P&L</MobileTableCard.Label>
						<span
							className={cm(
								'typo-label-md whitespace-nowrap',
								signDependentValue(pnl, {
									positive: 'text-text-positive-green-button',
									negative: 'text-text-negative-red-button',
									zero: 'text-text-default',
								})
							)}
						>
							{pnlNotional}
						</span>
					</div>
				</div>
			</MobileTableCard.CollapsibleHeader>
			<MobileTableCard.CollapsibleContent>
				<div className="flex flex-col gap-y-lg">
					<div className="flex justify-between">
						<div className="flex flex-col gap-y-xs">
							<MobileTableCard.Label>Size</MobileTableCard.Label>
							<span className="typo-label-md">
								{baseClosedForPnl?.prettyPrint()}{' '}
								{uimarket.uiSymbols.baseAssetSymbol}
							</span>
						</div>

						<div className="flex flex-col items-end gap-y-1">
							<div className="flex flex-col items-end gap-y-1">
								<PnlBreakdownTooltip
									pnl={pnl}
									pnlNotional={pnlNotional}
									positionPnlNotional={positionPnlNotional}
									feesAndFundingNotional={feesAndFundingNotional}
								>
									<MobileTableCard.Label
										className={TOOLTIP_TRIGGER_DECORATION_CLASSNAME}
									>
										P&L
									</MobileTableCard.Label>
								</PnlBreakdownTooltip>
								<span
									className={cm(
										'typo-label-md',
										signDependentValue(pnl, {
											positive: 'text-text-positive-green-button',
											negative: 'text-text-negative-red-button',
											zero: 'text-text-default',
										})
									)}
								>
									{pnlNotional}
								</span>
							</div>
						</div>
					</div>

					<div className="grid grid-cols-3">
						<MobileTableCard.LabelWithValue
							label="Entry Price"
							value={entryPrice.toNotional(
								true,
								undefined,
								priceDecimalOverride
							)}
						/>
						<MobileTableCard.LabelWithValue
							label="Exit Price"
							className="items-center"
							value={avgExitPrice.toNotional(
								true,
								undefined,
								priceDecimalOverride
							)}
						/>
						<MobileTableCard.LabelWithValue
							className="items-end"
							label={
								<DefinitionTooltip
									decoration="none"
									definitionId="positionHistoryPricePercentChange"
								>
									<MobileTableCard.Label
										className={TOOLTIP_TRIGGER_DECORATION_CLASSNAME}
									>
										Price %
									</MobileTableCard.Label>
								</DefinitionTooltip>
							}
							value={pricePercentChangeDisplay}
						/>
					</div>

					<div className="flex items-center gap-x-xs">
						<MobileTableCard.Label>Time</MobileTableCard.Label>
						<IconButton
							size="sm"
							variant="tertiary"
							className="-m-md p-md"
							onClick={onToggleDateDisplay}
							icon={Swap}
						/>
						<TableV2.DateCell
							className="w-fit py-0 whitespace-nowrap"
							displayAsTimestamp={displayDatesAsTimestamp}
							date={date}
						/>
					</div>

					<Button
						size="sm"
						className="w-full"
						variant="secondary"
						onClick={onShareClick}
					>
						Share
					</Button>
				</div>
			</MobileTableCard.CollapsibleContent>
		</MobileTableCard.Root>
	);
}
